import { useState, useCallback, useMemo } from 'react';
import type { NewCheckout } from '@lemonsqueezy/lemonsqueezy.js';
import type {
  LemonSqueezySubscription,
  LemonSqueezyCheckout,
} from '../lib/types';
import { toast } from 'sonner';
import { useFunctions } from './use-firebase';
import { httpsCallable } from 'firebase/functions';

/**
 * Custom hook for Lemon Squeezy operations
 *
 * All API calls are proxied through Firebase Cloud Functions to avoid CORS issues
 * and keep the API token secure.
 *
 * Provides methods for:
 * - Creating checkout sessions
 * - Managing subscriptions (get, cancel, update/change plan, resume)
 */
export const useLemonSqueezy = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const functions = useFunctions();

  // Memoize Cloud Function references to prevent unnecessary re-renders
  // This is critical to avoid infinite loops in useEffect dependencies
  const { createCheckoutFn, getSubscriptionFn, cancelSubscriptionFn, updateSubscriptionFn } = useMemo(() => ({
    createCheckoutFn: httpsCallable(functions, 'createCheckout'),
    getSubscriptionFn: httpsCallable(functions, 'getSubscription'),
    cancelSubscriptionFn: httpsCallable(functions, 'cancelSubscription'),
    updateSubscriptionFn: httpsCallable(functions, 'updateSubscription'),
  }), [functions]);

  /**
   * Create a checkout session for a product variant
   *
   * @param storeId - Your Lemon Squeezy store ID
   * @param variantId - The product variant ID to purchase
   * @param checkoutOptions - Additional checkout options (email, name, custom data, etc.)
   * @returns Checkout URL and ID
   */
  const createCheckoutSession = useCallback(
    async (
      storeId: string,
      variantId: string,
      checkoutOptions?: NewCheckout
    ): Promise<LemonSqueezyCheckout | null> => {
      setLoading(true);
      setError(null);

      try {
        // Call cloud function using httpsCallable
        const response = await createCheckoutFn({
          storeId,
          variantId,
          checkoutData: checkoutOptions?.checkoutData || {},
        });

        // httpsCallable returns data directly in response.data
        const data = response.data as any;

        if (!data || !data.checkoutUrl) {
          throw new Error('No checkout URL returned from server');
        }

        const checkout: LemonSqueezyCheckout = {
          checkoutUrl: data.checkoutUrl,
          checkoutId: data.checkoutId,
        };

        return checkout;
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to create checkout session';
        setError(errorMessage);
        toast.error(errorMessage);
        console.error('Checkout error:', err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createCheckoutFn]
  );

  /**
   * Get a subscription by ID
   *
   * @param subscriptionId - The subscription ID
   * @returns Subscription data
   */
  const fetchSubscription = useCallback(
    async (subscriptionId: string): Promise<LemonSqueezySubscription | null> => {
      setLoading(true);
      setError(null);

      try {
        const response = await getSubscriptionFn({ subscriptionId });
        const data = response.data as LemonSqueezySubscription;

        if (!data) {
          throw new Error('No subscription data returned');
        }

        return data;
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to fetch subscription';
        setError(errorMessage);
        console.error('Lemon Squeezy API Error:', err);
        console.warn('No Lemon Squeezy data returned');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [getSubscriptionFn]
  );

  /**
   * Cancel a subscription
   *
   * @param subscriptionId - The subscription ID to cancel
   * @returns Updated subscription data
   */
  const cancelUserSubscription = useCallback(
    async (subscriptionId: string): Promise<LemonSqueezySubscription | null> => {
      setLoading(true);
      setError(null);

      try {
        const response = await cancelSubscriptionFn({ subscriptionId });
        const data = response.data as LemonSqueezySubscription;

        if (!data) {
          throw new Error('No subscription data returned');
        }

        toast.success('Subscription cancelled successfully');
        return data;
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to cancel subscription';
        setError(errorMessage);
        toast.error(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [cancelSubscriptionFn]
  );

  /**
   * Change subscription plan (upgrade/downgrade)
   *
   * @param subscriptionId - The subscription ID
   * @param newVariantId - The new variant ID to switch to
   * @returns Updated subscription data
   */
  const changePlan = useCallback(
    async (
      subscriptionId: string,
      newVariantId: number
    ): Promise<LemonSqueezySubscription | null> => {
      setLoading(true);
      setError(null);

      try {
        const response = await updateSubscriptionFn({
          subscriptionId,
          variantId: newVariantId,
        });
        const data = response.data as LemonSqueezySubscription;

        if (!data) {
          throw new Error('No subscription data returned');
        }

        toast.success('Plan changed successfully');
        return data;
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to change plan';
        setError(errorMessage);
        toast.error(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [updateSubscriptionFn]
  );

  /**
   * Resume a cancelled subscription
   *
   * @param subscriptionId - The subscription ID to resume
   * @returns Updated subscription data
   */
  const resumeUserSubscription = useCallback(
    async (subscriptionId: string): Promise<LemonSqueezySubscription | null> => {
      setLoading(true);
      setError(null);

      try {
        const response = await updateSubscriptionFn({
          subscriptionId,
          cancelled: false,
        });
        const data = response.data as LemonSqueezySubscription;

        if (!data) {
          throw new Error('No subscription data returned');
        }

        toast.success('Subscription resumed successfully');
        return data;
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to resume subscription';
        setError(errorMessage);
        toast.error(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [updateSubscriptionFn]
  );

  return {
    loading,
    error,
    createCheckoutSession,
    fetchSubscription,
    cancelUserSubscription,
    changePlan,
    resumeUserSubscription,
  };
};
